import { useAxios, useDateAvailableRange, useNextTagIds } from 'api';
import {
  ApiDataExportDimensionsAndMetricsSchema,
  DataExportDimensionId,
  DataExportDimensionsAndMetrics,
  EnvironmentId,
  NewTagId,
} from 'api/apiTypes';
import { DimensionsAndCountriesResponseSchema } from 'api/apiTypes/dataExport/dimensionsAndCountries';
import { TagVariablesResponseSchema } from 'api/apiTypes/dataExport/dimensionsAndCountries/tagVariables';
import { dimensionSort, getDimensionId } from 'api/apiTypes/dataExport/dimensionsAndCountries/utils';
import {
  Dimension,
  TagBestPracticeDimension,
  TagOnlyDimension,
  TagVariableDimension,
} from 'api/apiTypes/dataExport/dimensionsAndCountries/viewModels';
import { DataExportHistory, DataExportHistorySchema } from 'api/apiTypes/dataExport/history';
import routes from 'api/routes';
import moment from 'moment';
import useSWR, { SWRResponse, useSWRConfig } from 'swr';

export function useDataExportDimensionsAndMetrics(
  envGuid: EnvironmentId | null
): SWRResponse<DataExportDimensionsAndMetrics> {
  const { get } = useAxios();

  const DIMENSIONS_AND_METRICS_TIMEOUT_MS = 90_000;
  return useSWR(
    envGuid ? routes.dataExport.getDimensionsAndMetrics(envGuid ?? ('' as EnvironmentId)) : null,
    async (url) => {
      const response = await get(url, {
        withCredentials: true,
        timeout: DIMENSIONS_AND_METRICS_TIMEOUT_MS,
      });
      return ApiDataExportDimensionsAndMetricsSchema.parse(response.data);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      errorRetryCount: 3,
      errorRetryInterval: 5000,
    }
  );
}

const DIMENSIONS_AND_COUNTRIES_TIMEOUT_MS = 90_000;

export function useDataExportDimensionsAndCountries() {
  const { get } = useAxios();
  return useSWR(
    routes.dataExport.getDimensionsAndCountries(),
    async (url) => {
      const response = await get(url, {
        withCredentials: true,
        timeout: DIMENSIONS_AND_COUNTRIES_TIMEOUT_MS,
      });
      const data = DimensionsAndCountriesResponseSchema.parse(response.data);
      return data;
    },
    {
      errorRetryCount: 3,
      errorRetryInterval: 5000,
    }
  );
}

const PiiTag: Dimension = {
  source: 'tags.variableConfigs',
  id: 'tag.4llTG5' as DataExportDimensionId,
  friendlyName: 'All Network Requests',
  tagId: '4llTG5' as NewTagId,
  tagOnly: true,
  group: 'Tag Variables',
  uiFlags: {
    showInConsentCategory: false,
    showInConsentTagMapping: false,
    showInSegments: false,
    showInDimensions: true,
    showInBestPracticeCustomization: false,
    showInQualifiers: false,
  },
};

export function useDataExportTags(envGuid: EnvironmentId | null) {
  const dateRange = useDateAvailableRange();
  const tagIds = useNextTagIds(envGuid, dateRange.maxRange, true);

  const finalData = {
    ...tagIds,
    data: tagIds.data?.map<TagOnlyDimension>((x) => {
      const dimension: TagOnlyDimension = {
        id: '' as DataExportDimensionId,
        friendlyName: x.tagName,
        source: 'tags.variableConfigs',
        tagId: x.tagId as NewTagId,
        group: 'Tag Variables',
        tagOnly: true,
      };
      dimension.id = getDimensionId(dimension);
      return dimension;
    }),
  };

  const dimension: TagOnlyDimension = {
    id: '' as DataExportDimensionId,
    friendlyName: 'Unidentified Requests',
    source: 'tags.variableConfigs',
    tagId: '' as NewTagId,
    group: 'Tag Variables',
    tagOnly: true,
  };

  dimension.id = getDimensionId(dimension);
  finalData.data?.push(dimension);

  // eslint-disable-next-line sentinelinsights/no-mutations
  finalData.data?.sort(dimensionSort);

  // eslint-disable-next-line sentinelinsights/no-mutations
  finalData.data?.unshift(PiiTag as TagOnlyDimension);

  return finalData;
}

function getEmptyTagIdVariables(): { bestPractices: TagBestPracticeDimension[]; variables: TagVariableDimension[] } {
  const hardcodedVariables = [
    ['full_url', 'Full URL'],
    // ['protocol', 'Protocol'],
    // ['domain', 'Domain'],
    // ['path_name', 'Path Name'],
    // ['query_string', 'Query String'],
    // ['hash', 'Hash'],
  ];

  const variables = hardcodedVariables.map<TagVariableDimension>(([path, name]) => {
    const dimension: TagVariableDimension = {
      id: '' as DataExportDimensionId,
      friendlyName: `${name} (${path})`,
      source: 'tags.variableConfigs',
      tagId: '' as NewTagId,
      variableId: '' as DataExportDimensionId,
      variablePath: path,
      group: 'Standard',
      tagOnly: false,
    };
    dimension.id = getDimensionId(dimension);
    return dimension;
  });

  return {
    bestPractices: [],
    variables,
  };
}

export function useDataExportTagVariablesAndBestPractices(envGuid: EnvironmentId | null, tagId: NewTagId | null) {
  const { get } = useAxios();

  const response = useSWR(
    envGuid && tagId ? routes.dataExport.getTagVariablesAndBestPractices(envGuid, tagId) : null,
    async (url) => {
      const response = await get(url, { withCredentials: true });
      const data = TagVariablesResponseSchema.parse(response.data);
      return {
        bestPractices: data.bestPractices.map<TagBestPracticeDimension>((apiDto) => {
          const dimension: TagBestPracticeDimension = {
            id: apiDto.id as DataExportDimensionId,
            source: 'tags.bestPracticeConfigs',
            commonBestPracticeId: apiDto.commonBestPracticeId,
            friendlyName: apiDto.title,
            tagId: tagId as NewTagId,
            lzStringCompressedId: apiDto.id,
            group: 'Best Practices',
          };
          return dimension;
        }),
        variables: [
          ...data.variables.map<TagVariableDimension>((apiDto) => {
            const dimension: TagVariableDimension = {
              id: '' as DataExportDimensionId,
              friendlyName: `${apiDto.friendly_name} (${apiDto.group_path})`,
              source: 'tags.variableConfigs',
              tagId: tagId as NewTagId,
              variableId: (apiDto.variable_id ?? '') as DataExportDimensionId,
              variablePath: apiDto.group_path,
              group: apiDto.category_friendly_name,
              tagOnly: false,
            };
            dimension.id = getDimensionId(dimension);
            return dimension;
          }),
        ],
      };
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      errorRetryCount: 3,
      errorRetryInterval: 5000,
    }
  );

  if (tagId === '') {
    return {
      ...response,
      data: getEmptyTagIdVariables(),
    };
  }

  return response;
}

export function useDataExportHistory(envGuid: EnvironmentId | null) {
  const { get } = useAxios();
  return useSWR(
    envGuid ? routes.dataExport.getHistory(envGuid) : null,
    async (url) => {
      const response = await get(url, { withCredentials: true });
      if (Array.isArray(response.data)) {
        const data = response.data
          .map((item) => {
            try {
              return DataExportHistorySchema.parse(item);
            } catch (error) {
              return null;
            }
          })
          .filter((item) => item !== null) as DataExportHistory[];

        // eslint-disable-next-line sentinelinsights/no-mutations
        return data.sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)));
      }
      throw new Error('Invalid response from server');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      errorRetryCount: 3,
      errorRetryInterval: 5000,
    }
  );
}

export function useDataExportHistoryRefresh(envGuid: EnvironmentId | null) {
  const { mutate } = useSWRConfig();
  return () => {
    const url = envGuid ? routes.dataExport.getHistory(envGuid) : null;
    if (url) {
      mutate(url);
    }
  };
}
